import { motion } from "framer-motion";
import { Check, Nfc } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContext";
import { SectionHead } from "@/components/SectionHead";
import { DEFAULT_IMAGES } from "@/config";

export const Hardware = () => {
  const { t } = useLang();
  const { img } = useSite();
  const h = t.hw;

  return (
    <section id="hardware" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead kicker={h.kicker} title={h.title} sub={h.sub} />
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-[2rem] bg-pine p-8 text-cream sm:p-10"
            data-testid="hardware-terminal-card"
          >
            <div className="pointer-events-none absolute -end-24 -top-24 h-72 w-72 rounded-full bg-white/5" />
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10 text-sun">
              <Nfc className="h-6 w-6" />
            </span>
            <h3 className="mt-6 font-display text-2xl font-bold sm:text-3xl">{h.terminal.name}</h3>
            <p className="mt-1 text-sm font-semibold text-cream/60">{h.terminal.sub}</p>
            <ul className="mt-7 space-y-3.5">
              {h.terminal.feats.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-cream/90">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-sun" />
                  {f}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-[2rem] border border-cardline bg-white p-8 sm:p-10"
            data-testid="hardware-card-product"
          >
            <img src={img("img_card", DEFAULT_IMAGES.card)} alt={h.card.imgAlt} className="h-52 w-full rounded-2xl object-cover" />
            <h3 className="mt-6 font-display text-2xl font-bold text-ink sm:text-3xl">{h.card.name}</h3>
            <p className="mt-1 text-sm font-semibold text-inkmute">{h.card.sub}</p>
            <ul className="mt-7 space-y-3.5">
              {h.card.feats.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-inksoft">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-pine" />
                  {f}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
        <p className="mt-8 text-sm italic text-inkmute">{h.note}</p>
      </div>
    </section>
  );
};
